import {
  SortDirection,
  TableColumns,
} from "features/CenterContent/RoleContent/Lokleder/ReasonCodes/ReasonCodes";
import CodeAndComment from "features/CenterContent/RoleContent/Lokleder/ReasonCodes/TableContent/CodeAndComment";
import {
  filterRCList,
  sortRCList,
} from "features/CenterContent/RoleContent/Lokleder/ReasonCodes/TableContent/utils";
import {
  ReasonCodeItem,
  ReasonCodes,
} from "features/CenterContent/RoleContent/Lokleder/ReasonCodes/schemas";
import { FC, RefObject, useMemo } from "react";
import { TableCell, TableRow } from "shared/components/Spor";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { useInfiniteScroll } from "shared/hooks/useInfiniteScroll";
import {
  formatDayDateString,
  formatTrainDate,
} from "shared/utils/dateTimeUtils";
import styled, { useTheme } from "styled-components";

const DelaySpan = styled.span`
  margin-left: 3px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

export type SortOptions = {
  sortColumn: keyof ReasonCodeItem;
  sortDirection: SortDirection;
};

type TableContentProps = {
  data: ReasonCodes;
  onRowClick: (reasonCode: ReasonCodeItem) => void;
  trainFilter: string;
  codeFilter: string;
  sortOptions: SortOptions;
  TableContainerRef: RefObject<HTMLDivElement | null>;
};

const TableContent: FC<TableContentProps> = ({
  data,
  onRowClick,
  trainFilter,
  codeFilter,
  sortOptions,
  TableContainerRef,
}) => {
  const theme = useTheme();
  const filteredAndSortedRC = useMemo(
    () =>
      data
        .filter((rc) => filterRCList(rc, trainFilter, codeFilter))
        .sort(sortRCList(sortOptions)),
    [
      data,
      trainFilter,
      codeFilter,
      sortOptions.sortColumn,
      sortOptions.sortDirection,
    ],
  );

  const { numberOfItemsToRender } = useInfiniteScroll({
    initialItemsToRender: 25,
    containerRef: TableContainerRef,
    maxItemsToRender: filteredAndSortedRC.length,
  });

  if (filteredAndSortedRC.length <= 0) {
    return (
      <TableRow>
        <TableCell colSpan={TableColumns.length} p="30px">
          <NoDataMessage reason="Fant ingen årsakskoder som matcher aktivt filter" />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {filteredAndSortedRC.slice(0, numberOfItemsToRender).map((rc) => {
        const isConductor = rc.employeeRole === "CONDUCTOR";
        const isDriver = rc.employeeRole === "DRIVER";
        return (
          <TableRow
            key={rc.id}
            onClick={() => onRowClick(rc)}
            cursor="pointer"
            _hover={{ backgroundColor: theme.colorBackgroundSecondary }}
          >
            <TableCell p="6px">{formatTrainDate(rc.trainDate)}</TableCell>
            <TableCell p="6px">{rc.trainNumber}</TableCell>
            <TableCell p="6px">
              {formatDayDateString(rc.registeredAt)}
            </TableCell>
            <TableCell p="6px">
              {rc.stationName}
              {rc.delay && rc.delay > 0 && (
                <DelaySpan>{`(+${rc.delay})`}</DelaySpan>
              )}
            </TableCell>
            <TableCell p="6px">
              {isDriver && (
                <CodeAndComment
                  code={rc.employeeReasonCode}
                  comment={rc.employeeComment}
                />
              )}
            </TableCell>
            <TableCell p="6px">
              {isConductor && (
                <CodeAndComment
                  code={rc.employeeReasonCode}
                  comment={rc.employeeComment}
                />
              )}
            </TableCell>
            <TableCell p="6px">
              <CodeAndComment
                code={rc.dropsReasonCode}
                comment={rc.dropsComment}
              />
            </TableCell>
            <TableCell p="6px">
              <CodeAndComment
                code={rc.tiosReasonCode}
                comment={rc.tiosComment}
              />
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
};

export default TableContent;
